import { Icon } from 'components/common';
import CoinProgressBar from 'components/promotion/CoinProgressBar';
import CoinPurchaseAnimation, { CoinPurchaseAnimationRef } from 'components/promotion/CoinPurchaseAnimation';
import useBottomSheet from 'hooks/common/useBottomSheet';
import { useLottiePlayer } from 'hooks/common/useLottiePlayer';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ProductItem from './components/ProductItem';

const PROMO_PRODUCT_IDS = ['ccoin_1200', 'ccoin_33000'];
const UNSUPPORTED_PRODUCT_IDS = ['ccoin_100', 'ccoin_200', 'ccoin_250', 'ccoin_300', 'ccoin_777'];
const TOP_BENEFIT_PRODUCT_ID = 'ccoin_33000';
const LITE_BENEFIT_PRODUCT_ID = 'ccoin_1200';

const ANIMATION_DELAY = 100;

const CoinAllPromotion: React.FC = () => {
  const { close, sheets, updateOptions, updateData } = useBottomSheet();
  const sheet = sheets.CoinAllPromotion;
  const { bonusRatio = 0, imageLoaded } = sheet.data;
  const [promoList, setPromoList] = useState<any[]>([]);
  const [coinList, setCoinList] = useState<any[]>([]);

  const coinPurchaseAnimationRef = useRef<CoinPurchaseAnimationRef>(null);

  useEffect(() => {
    Device.getProductInfo((data: any) => {
      const promoList: any[] = [];
      const coinList: any[] = [];
      const json = JSON.parse(data) as any[];
      json.forEach((item) => {
        const jsoinItem = JSON.parse(item);

        if (!jsoinItem.productId.startsWith('ccoin')) return;
        if (UNSUPPORTED_PRODUCT_IDS.includes(jsoinItem.productId)) return;
        if (PROMO_PRODUCT_IDS.includes(jsoinItem.productId)) {
          promoList.push(jsoinItem);
          return;
        }

        coinList.push(jsoinItem);
      });
      setPromoList(promoList.sort((a, b) => a.price_amount - b.price_amount));
      setCoinList(coinList.sort((a, b) => a.price_amount - b.price_amount));
    });

    return () => {
      PurchaseManager.detach();
      PageShop.resetPromo();
    };
  }, []);

  useEffect(() => {
    updateOptions(sheet.id, {
      containerStyle: {
        animation: 'highlight 2s infinite',
        animationFillMode: 'forwards',
        backgroundSize: '800% 100%',
        backgroundImage: 'linear-gradient(-75deg, transparent 0%, transparent 12.5%, rgba(255,255,255, 0.1) 17.5%, transparent 30%)',
      },
    });

    if (promoList.length > 0) {
      const img = new Image();
      img.src = PageShop.getCoinURL(promoList[0].productId);
      img.onload = () => {
        updateData(sheet.id, { imageLoaded: true });
        updateOptions(sheet.id, {
          containerStyle: {
            animation: 'none',
            backgroundSize: '',
            backgroundImage: '',
          },
        });
      };
    }
  }, [promoList, updateOptions, sheet.id, updateData]);

  useEffect(()=>{
    Braze.applyPageShopProtocol(new URL(`thebermuda://deeplink/promo?bonus_ratio=${bonusRatio}`));
    PurchaseManager.attach(function (err: any, coin: any) {
      if (err) return;

      coinPurchaseAnimationRef.current?.show().then(() => {
        close(sheet.id, { skipConfirm: true });
      });
    });
  },[bonusRatio, close, sheet.id, coinPurchaseAnimationRef]);

  const onClick = (productId: string) => {
    PurchaseManager.buyCoin(productId);

     ba('fcm-popup', undefined, {
       popup: sheet.id,
       actionType: 'clicked-purchase',
       productId,
       result: sheet.data,
       fbUid: myinfo.fbUid,
       country: myinfo.country,
       device: Device.name,
     });
  };

  return (
    <div className="flex w-full flex-col text-white">
      <div className="flex h-[56px] w-full">
        <Icon name="icon/ic_close.svg" onClick={() => close(sheet.id, { skipConfirm: false })} color="filter-base-ic-white" />
      </div>
      <CoinProgressBar loaded={imageLoaded} />
      <CoinLottie />
      <AnimationText
        classicName="typography-title-md mb-4"
        text={brsc.bonus_50_title.replace('<span>', '<span class="text-[#FFC539]">').replace('50%', `${bonusRatio}%`)}
        animationDelayCount={6}
      />
      <AnimationText classicName="typography-headline-sm" text={brsc.bonus_50_sub_1} animationDelayCount={6} />
      <CoinGridView list={promoList} isPromo={true} animationDelayCount={7} onClick={onClick} bonusRatio={bonusRatio} />
      <AnimationText classicName="typography-headline-sm" text={brsc.bonus_50_sub_2.replace('50%', `${bonusRatio}%`)} animationDelayCount={15} />
      <CoinGridView list={coinList} animationDelayCount={11} onClick={onClick} bonusRatio={bonusRatio} />
      <div className="h-[100px]"></div>
      <CoinPurchaseAnimation ref={coinPurchaseAnimationRef} />
    </div>
  );
};

const CoinLottie: React.FC = React.memo(() => {
  const { playerRef, playOnce } = useLottiePlayer({ delay: 500, speed: 1, loop: false });

  useEffect(() => {
    playOnce();
  }, [playOnce]);

  return <lottie-player ref={playerRef} src="/imgweb/lottie/coin_promotion.json" className="h-full w-full" />;
});

const AnimationText: React.FC<{ text: string; classicName?: string; animationDelayCount: number }> = ({ text, classicName = '', animationDelayCount }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setVisible(true);
    }, animationDelayCount * ANIMATION_DELAY);
  }, [animationDelayCount]);

  return (
    <div
      key={animationDelayCount}
      className={`transform transition-all duration-500 ${visible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'} ${classicName}`}
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );
};

type CoinGridViewProps = {
  list: any[];
  isPromo?: boolean;
  animationDelayCount: number;
  bonusRatio?: number;
  onClick: (productId: string) => void;
};

const CoinGridView: React.FC<CoinGridViewProps> = ({ list, isPromo = false, animationDelayCount, bonusRatio, onClick }) => {
  const [visibleRows, setVisibleRows] = useState<number>(0);
  const rows = useMemo(() => {
    const result: any[] = [];
    for (let i = 0; i < list.length; i += 2) {
      result.push(list.slice(i, i + 2));
    }
    return result;
  }, [list]);

  useEffect(() => {
    if (list.length === 0) return;

    setVisibleRows(0);
    const timeouts: NodeJS.Timeout[] = [];

    rows.forEach((row, index) => {
      const timeout = setTimeout(
        () => {
          setVisibleRows((prev) => prev + 1);
        },
        (animationDelayCount + index) * ANIMATION_DELAY
      );
      timeouts.push(timeout);
    });

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [rows, animationDelayCount, list.length]);

 const getPromoType = (productId: string) => {
  if(productId === TOP_BENEFIT_PRODUCT_ID) {
    return 'top';
  }

  if(productId === LITE_BENEFIT_PRODUCT_ID) {
    return 'lite';
  }

  return null;
 };

  return (
    <div className="mt-1 flex w-full flex-col gap-2 text-white">
      <div className="flex flex-col gap-2">
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={`grid transform grid-cols-2 gap-2 transition-all duration-500 ${rowIndex < visibleRows ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
          >
            {row.map((item: any) => (
              <ProductItem key={item.productId} item={item} promoType={getPromoType(item.productId)} bonusRatio={bonusRatio} onClick={onClick} useTheme={false} />
            ))}
          </div>
        ))}
      </div>
      <div className="h-[24px]"></div>
    </div>
  );
};


export default React.memo(CoinAllPromotion);
